import document from 'common/document'

import { ITitleAndDescriptionArgs } from './types'

interface IGetSEOResponse {
  description?: string
  title?: string
  keywords?: string
}

// ELEMENTS USED TO BUILD PAGE TITLE IN TAB
const DELIMITER = ' · '
const APP_NAME = 'Scoir'

// DEFAULT META DESCRIPTION CONTENT
const DEFAULT_DESCRIPTION =
  'Scoir is a solution for students, parents, counselors, and colleges focused on finding the right fit through a unified college search and planning network.'

// DEFAULT META KEYWORDS CONTENT
const DEFAULT_KEYWORDS = [
  'Scoir',
  'College',
  'Search',
  'Planning',
  'Network',
  'Students',
  'Parents',
  'Counselors',
  'Colleges',
]

/**
 * @description getMetaTag('name')
 * @returns {Element} - <meta name="name" content="..." /> found in the document
 */
const getMetaTag = (name: string) => {
  return document.querySelector(`meta[name="${name}"]`)
}

/**
 * @description getDescriptionMetaTag
 * @returns {Element} - <meta name="description" content="..." /> found in the document
 */
const getDescriptionMetaTag = () => {
  return getMetaTag('description')
}

/**
 * @description getKeywordsMetaTag
 * @returns {Element} - <meta name="keywords" content="..." /> found in the document
 */
const getKeywordsMetaTag = () => {
  return getMetaTag('keywords')
}

/**

/**
 * @description getDescription
 * @returns {string} - the content of the <meta name="description" content="..." /> tag
 */
const getDescription = () => {
  return getDescriptionMetaTag()?.getAttribute('content')
}

/**
 * @description setDescription
 * @param {string} description - the new description to set
 */
const setDescription = (description: string) => {
  getDescriptionMetaTag()?.setAttribute('content', description)
}

/**
 * @description getKeywords
 * @returns {string} - the content of the <meta name="keywords" content="..." /> tag
 */
const getKeywords = () => {
  return getKeywordsMetaTag()?.getAttribute('content')
}

/**
 * @description setKeywords
 * @param {string} keywords - the new keywords to set
 */
const setKeywords = (keywords: string) => {
  getKeywordsMetaTag()?.setAttribute('content', keywords)
}

/**
 * @description getKeywordsString
 * @param {string[]} title - an array of strings to be joined with the delimiter
 * @returns {string} - the keyword string to be used in the document
 */
const getKeywordsString = (keywords: string[] = []): string => {
  return keywords.join(', ')
}

/**
 * @description getTitle
 * @returns {string} - the current document title
 */
const getTitle = (): string => {
  return document.title
}

/**
 * @description getTitleString
 * @param {string[]} title - an array of strings to be joined with the delimiter
 * @returns {string} - the title string to be used in the document
 */
const getTitleString = (title: string[] = []): string => {
  return [...title, APP_NAME].filter(Boolean).join(DELIMITER)
}

/**
 * @description setTitle
 * @param {string} title - the new title to set
 */
const setTitle = (title: string) => {
  document.title = title
}

/**
 * @description getSEO
 * @returns {object} - { description: string, title: string, keywords: string}
 */
export const getSEO = (): IGetSEOResponse => {
  const description = getDescription() || undefined
  const title = getTitle() || undefined
  const keywords = getKeywords() || undefined
  return { description, title, keywords }
}

/**
 * @description checkSEOMatch
 * @param {ITitleAndDescriptionArgs} args - the title and description to compare against the current document title and description
 * @returns {boolean} - true if the title and description match the current document title and description
 */
export const checkSEOMatch = ({
  title = [],
  description = DEFAULT_DESCRIPTION,
  keywords = [],
}: ITitleAndDescriptionArgs) => {
  const current = getSEO()
  const descriptionMatches = current.description === description
  const titleMatches = current.title === getTitleString(title)
  const keywordsMatches = current.keywords === getKeywordsString(keywords)
  return descriptionMatches && titleMatches && keywordsMatches
}

/**
 * @description setSEO
 * @param {ITitleAndDescriptionArgs} args - the title and description to set
 */
export const setSEO = ({ title = [], description = DEFAULT_DESCRIPTION, keywords = [] }: ITitleAndDescriptionArgs) => {
  setTitle(getTitleString(title))
  setDescription(description || DEFAULT_DESCRIPTION)
  setKeywords(getKeywordsString(keywords) || getKeywordsString(DEFAULT_KEYWORDS))
}
